/**
 * Button (docs/14): primary = solid purple, white text; ghost = 1px cyan border; action = small
 * with a mono label (table rows, cards); secondary = neutral outline; danger for destructive
 * confirmations. 4px radius, no shadows; focus shows the cyan signal ring.
 */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type ButtonVariant = 'primary' | 'ghost' | 'action' | 'secondary' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--oex-purple)] text-white border border-[var(--oex-purple)] hover:brightness-110',
  ghost:
    'bg-transparent text-foreground border border-[var(--oex-cyan)] hover:bg-[color-mix(in_srgb,var(--oex-cyan)_10%,transparent)]',
  action:
    'font-data text-xs uppercase tracking-wide bg-transparent border border-border text-foreground hover:border-[var(--oex-cyan)]',
  secondary: 'bg-card text-card-foreground border border-border hover:bg-accent',
  danger: 'bg-destructive text-destructive-foreground border border-destructive hover:brightness-110',
  link: 'bg-transparent border-0 text-[var(--oex-purple)] underline-offset-4 hover:underline px-0',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-9 px-4 text-sm gap-2',
  lg: 'h-11 px-6 text-base gap-2',
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    className,
    disabled,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-[4px] font-medium transition-colors signal-focus',
        'disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
        VARIANTS[variant],
        variant === 'action' ? 'h-7 px-2.5' : SIZES[size],
        className,
      )}
      {...props}
    >
      {loading ? (
        <span className="size-3 animate-spin rounded-full border-2 border-current border-r-transparent" />
      ) : null}
      {children}
    </button>
  );
});
